import { GlobalHelper } from '../utils/GlobalHelper';
import { AppDispatcher } from '../dispatcher/AppDispatcher';
import { ActionConstants } from '../constants/ActionConstants';
import { ProjectManagerActions, ProjectAttributes } from './ProjectManagerActions';
import { ArticleActions } from './ArticleActions';
import { FLOOR_LIGHT, FLOOR_DARK, FLOOR_MIDDLE } from '../constants/FloorConstants';
import { SidebarModule, SaveType } from '../constants/Enums';
import { SceneStore } from '../stores/SceneStore';
import { ViewSettingsActions } from './ViewSettingsActions';
import { SessionActions } from './SessionActions';
import { ProjectManagerStore } from '../stores/ProjectManagerStore';
import { DownloadHelper } from '../utils/DownloadHelper';
import { PersistentActions } from './PersistentActions';
import { ProjectHelper } from '../utils/ProjectHelper';
import { PconLoginActions } from './PconLoginActions';
import { ProjectSettingsStore } from '../stores/ProjectSettingsStore';
import { PersistentStore } from '../stores/PersistentStore';
import { CameraHelper } from '../utils/CameraHelper';
import { FloorPlanActions } from './FloorPlanActions';
import { PconLoginIdentifier, PconLoginStore } from '../stores/PconLoginStore';
import { ArStore } from '../stores/ArStore';

import cf = egr.wcf.cf;
import core = egr.wcf.core;
import utils = egr.wcf.utils;
import room = egr.wcf.room;
import { notNull } from '../submodules/utils/Types';

interface OpenSceneArguments {
    projectId?: string;
    projectAttributes?: ProjectAttributes;
    projectLoadingHook?: () => Promise<void>;
    validateProjectAttibutesIfNull?: boolean; // default true
    validateProjectAttibutesWithoutProjectManager?: boolean;
}

export abstract class SceneActions {
    public static set2dWallMode(): void {
        GlobalHelper.roomManager.setVisibilityMode(room.VisibilityMode.Show2d);
        AppDispatcher.dispatch({
            actionType: ActionConstants.SET_3D_WALLMODE,
            value: false
        });
    }

    public static set3dWallMode(): void {
        GlobalHelper.roomManager.setVisibilityMode(room.VisibilityMode.Show3d);
        AppDispatcher.dispatch({
            actionType: ActionConstants.SET_3D_WALLMODE,
            value: true
        });
    }

    public static MaximizeScene(): void {
        if (!GlobalHelper.activeView.isMaximized) {
            GlobalHelper.activeView.maximize();
            AppDispatcher.dispatch({
                actionType: ActionConstants.MAXIMIZE_SCENE,
                value: true
            });
        }
    }

    public static MinimizeScene(): void {
        if (GlobalHelper.activeView.isMaximized) {
            GlobalHelper.activeView.minimize();
            AppDispatcher.dispatch({
                actionType: ActionConstants.MAXIMIZE_SCENE,
                value: false
            });
        }
    }

    public static setUnsavedChanges(value: boolean): void {
        if (SceneStore.getState().unsavedChanges !== value) { // avoid unnecessary store updates
            AppDispatcher.dispatch({
                actionType: ActionConstants.SET_UNSAVED_CHANGES,
                value: value
            });
        }
    }

    public static updateUndoRedo(): void {
        window.setTimeout(() => {
            const canUndo: boolean = GlobalHelper.coreApp.commands.canUndo;
            const canRedo: boolean = GlobalHelper.coreApp.commands.canRedo;
            if (
                SceneStore.getState().canRedo !== canRedo ||
                SceneStore.getState().canUndo !== canUndo
            ) {
                AppDispatcher.dispatch({
                    actionType: ActionConstants.UPDATE_UNDO_REDO,
                    value: {canUndo: canUndo, canRedo: canRedo}
                });
            }
        });
    }

    public static markSceneAsChanged(): void {
        SceneActions.setUnsavedChanges(true);
    }

    public static setViewerDimension(): void {
        AppDispatcher.dispatch({
            actionType: ActionConstants.SET_VIEWER_DIMENSIONS,
            value: GlobalHelper.coreApp.viewer
        });
    }

    public static setActiveView(): void {
        AppDispatcher.dispatch({
            actionType: ActionConstants.SET_ACTIVE_VIEW,
            value: GlobalHelper.activeView.overlayDiv
        });
    }

    public static updateSelection(): void {
        const elements: ReadonlyArray<core.mdl.SceneElement> = GlobalHelper.coreApp.model.selection;
        const ids: Array<string> = elements.filter(SceneActions.isArticleElement).map((articleElement: cf.ArticleElement): string => {
            const selectedSubArticle: core.mdl.SceneElement | null = GlobalHelper.coreApp.model.getSubElementSelection(articleElement);
            if (selectedSubArticle != null && SceneActions.isArticleElement(selectedSubArticle)) {
                return selectedSubArticle.basketId == null ? '' : selectedSubArticle.basketId;
            }
            return articleElement.basketId == null ? '' : articleElement.basketId;
        });

        AppDispatcher.dispatch({
            actionType: ActionConstants.UPDATE_SELECTION,
            value: {elements: elements, ids: ids}
        });
    }

    public static deleteSelectedElements(): Promise<void> {
        if (GlobalHelper.coreApp.model.selection.length !== 0) {
            GlobalHelper.stopActiveTool();

            const command: core.cmd.DeleteElements = new core.cmd.DeleteElements(
                GlobalHelper.coreApp,
                GlobalHelper.coreApp.model.selection
            );

            if (command.isAvailable()) {
                return GlobalHelper.coreApp.commands.executeCommand(command).then(() => {
                    ArticleActions.invalidateArticleList();
                    if (GlobalHelper.getSceneElementsCount() === 0) {
                        GlobalHelper.arManager.core.leaveAr();
                    }
                }).catch((error) => {
                    utils.Log.error('Failed to delete selection: ' + error);
                });
            }
        }

        return Promise.resolve();
    }

    public static saveSelection(): void {
        AppDispatcher.dispatch({
            actionType: ActionConstants.SAVE_SELECTION,
            value: GlobalHelper.coreApp.model.selection
        });
    }

    public static cloneSavedElements(): Promise<void> {
        const elements: Array<core.mdl.SceneElement> = SceneStore.getState().savedSelection;
        return SceneActions.cloneElements(elements);
    }

    public static cloneElements(elements: Array<core.mdl.SceneElement>): Promise<void> {
        const filteredElements: Array<core.mdl.SceneElement> = elements.filter((checkedElement) => {
            return checkedElement.category !== core.mdl.ElementCategory.Wall && checkedElement.category !== core.mdl.ElementCategory.WallOpening;
        });
        if (filteredElements.length !== 0) {
            return ViewSettingsActions.withLock<void>((): Promise<void> => {
                GlobalHelper.stopActiveTool();

                const clones: Array<core.mdl.SceneElement> = elements.map(
                    (element: core.mdl.SceneElement, index: number, values: Array<core.mdl.SceneElement>) => {
                        return element.clone();
                    }
                ).filter(notNull);

                GlobalHelper.placeElementsInViewCenter(clones);

                const command: core.cmd.InsertElements = new core.cmd.InsertElements(GlobalHelper.coreApp, clones);
                return GlobalHelper.coreApp.commands.executeCommand(command).then((): void => {
                    GlobalHelper.coreApp.model.setSelection(clones);
                    ArticleActions.invalidateArticleList();
                });
            });
        }
        return Promise.resolve();
    }

    public static cloneSelectedElements(): Promise<void> {
        return SceneActions.cloneElements(GlobalHelper.coreApp.model.selection as Array<core.mdl.SceneElement>);
    }

    public static undo(): Promise<void> {
        if (GlobalHelper.coreApp.commands.canUndo) {
            GlobalHelper.stopActiveTool();

            return GlobalHelper.coreApp.commands.undo().then((): void => {
                ArticleActions.invalidateArticleList();
            }).catch((error) => {
                utils.Log.error('redo failed: ' + error);
                return Promise.reject(error);
            });
        }

        return Promise.resolve();
    }

    public static redo(): Promise<void> {
        if (GlobalHelper.coreApp.commands.canRedo) {
            GlobalHelper.stopActiveTool();

            return GlobalHelper.coreApp.commands.redo().then((): void => {
                ArticleActions.invalidateArticleList();
            }).catch((error) => {
                utils.Log.error('redo failed: ' + error);
                return Promise.reject(error);
            });
        }

        return Promise.resolve();
    }

    public static setFloorColor(color: string): void {
        switch (color) {
            case 'std-grey':
                (GlobalHelper.floorPlane.material as core.mdl.PbrMaterial).albedoColor = FLOOR_LIGHT.toLinearSpace();
                GlobalHelper.coreApp.viewer.requestRenderFrame();
                break;
            case 'dark-grey':
                (GlobalHelper.floorPlane.material as core.mdl.PbrMaterial).albedoColor = FLOOR_DARK.toLinearSpace();
                GlobalHelper.coreApp.viewer.requestRenderFrame();
                break;
            case 'middle-grey':
                (GlobalHelper.floorPlane.material as core.mdl.PbrMaterial).albedoColor = FLOOR_MIDDLE.toLinearSpace();
                GlobalHelper.coreApp.viewer.requestRenderFrame();
                break;
            default:
                break;
        }
    }

    public static async importPecFile(pecFile: Blob | ArrayBuffer): Promise<void> {
        return ViewSettingsActions.withLock(async (): Promise<void> => {
            ViewSettingsActions.changeHamburger(null);
            ViewSettingsActions.changeSidebar(SidebarModule.NONE);
            const elements: Array<core.mdl.SceneElement> = await SessionActions.loadPecFile(pecFile);
            GlobalHelper.coreApp.model.setSelection(elements);
            ArticleActions.invalidateArticleList();
        });
    }

    public static async saveScene(saveType: SaveType): Promise<boolean> {
        if (saveType === SaveType.DEVICE) {
            await DownloadHelper.downloadProjectContainer();
        }
        return true;
    }

    public static async closeScene(saveType: SaveType): Promise<boolean> {
        // save the current state first of all
        const sceneWasSaved: boolean = await SceneActions.saveScene(saveType);

        if (ArStore.getState().arDevice && ArStore.getState().started) {
            await ArStore.core.leaveAr();
        }

        if (sceneWasSaved) {
            // clear the auto save id before the active project is closed
            if (ProjectManagerStore.getCurrentProject() != null) {
                PersistentActions.setAutoSaveId('');
            }

            // Note: we have to reset all stores at the beginnng, because
            // the session start triggers new events which could result in
            // race conditions
            ProjectManagerActions.resetProjectReleatedSettings();

            GlobalHelper.coreApp.tools.startDefaultTool();

            // clear scene
            GlobalHelper.coreApp.document.clear();
            GlobalHelper.coreApp.viewer.requestRenderFrame();

            // close the actual eaiws session
            if (GlobalHelper.eaiwsSession.session != null) {
                await GlobalHelper.eaiwsSession.close();
            }
            return true;
        }
        return false;
    }

    public static async newScene(
        openSceneArguments: OpenSceneArguments | null = null,
        saveType: SaveType = SaveType.NOT,
        lockComponent: JSX.Element | null = null
    ): Promise<void> {
        return ViewSettingsActions.withLock<void>(
            async (): Promise<void> => {
                if (ArStore.getState().arDevice && ArStore.getState().started) {
                    await ArStore.core.leaveAr();
                }

                await SceneActions.closeScene(saveType);

                return SceneActions.openScene(openSceneArguments);
            },
            lockComponent
        );
    }

    protected static async openScene(openArgs: OpenSceneArguments | null): Promise<void> {
        const args: OpenSceneArguments = openArgs != null ? openArgs : {} as OpenSceneArguments;
        let usePackageGroups: boolean = true;
        if (
            (args.projectId != null || args.validateProjectAttibutesWithoutProjectManager === true) &&
            (args.projectAttributes != null || args.validateProjectAttibutesIfNull !== false)
        ) {
            // Note: we have to check the permission even if the attributes are missing
            usePackageGroups = await ProjectHelper.canLoadProject(args.projectAttributes);
        }

        PconLoginActions.usePackageGroups(usePackageGroups);
        await SessionActions.startEaiwsSession(
            ProjectSettingsStore.getState().dataLanguage,
            PersistentStore.getState().language
        );

        GlobalHelper.coreApp.viewer.requestRenderFrame();
        CameraHelper.resetCameras();
        FloorPlanActions.reset();

        if (args.projectLoadingHook != null) {
            await args.projectLoadingHook();
        }

        let identifier: PconLoginIdentifier | undefined;
        if (usePackageGroups) {
            identifier = PconLoginStore.getIdentifier();
        }
        // Note: `false` is used to differentiate between old projects without the `userinfo` app data
        // and new project without an active pCon.login session
        // (`null` can't be used because it will be removed by the serialization function)
        GlobalHelper.coreApp.document.appData.setData('pCon.login', 'userinfo', identifier != null ? identifier : false);

        ArticleActions.invalidateArticleList(true);
        ViewSettingsActions.changeSidebar(SidebarModule.NONE);
    }

    protected static isArticleElement(value: core.mdl.SceneElement): value is cf.ArticleElement {
        return value instanceof cf.ArticleElement;
    }
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/actions/SceneActions.ts